import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function usePushNotifications(userId) {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(false);

  // Registra o service worker e verifica estado inicial
  useEffect(() => {
    const sw =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;
    setSupported(sw);
    if (!sw || !userId) return;

    setPermission(Notification.permission);

    // Verifica se já existe subscription salva
    const checkSubscription = async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          // Confirma que ainda está salva no Supabase
          const { data } = await supabase
            .from('push_subscriptions')
            .select('id')
            .eq('user_id', userId)
            .eq('endpoint', sub.endpoint)
            .single();
          setIsSubscribed(!!data);
        }
      } catch (err) {
        console.error('Push init error:', err);
      }
    };
    checkSubscription();
  }, [userId]);

  const subscribe = useCallback(async () => {
    if (!userId || !supported) return;
    if (!('Notification' in window)) return;
    if (Notification.permission === 'denied') return;
    setLoading(true);

    try {
      // Garante que o SW está registrado antes de pedir permissão
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') { setLoading(false); return; }

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const subJson = sub.toJSON();
      const { endpoint, keys } = subJson;

      const { error } = await supabase.from('push_subscriptions').upsert({
        user_id: userId,
        endpoint,
        p256dh: keys.p256dh,
        auth: keys.auth,
      }, { onConflict: 'user_id,endpoint' });

      if (error) throw error;
      setIsSubscribed(true);
    } catch (err) {
      console.error('Push subscribe error:', err);
      alert('Não foi possível ativar as notificações: ' + (err?.message || String(err)));
    }
    setLoading(false);
  }, [userId, supported]);

  const unsubscribe = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('user_id', userId)
          .eq('endpoint', sub.endpoint);
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
    } catch (err) {
      console.error('Push unsubscribe error:', err);
    }
    setLoading(false);
  }, [userId]);

  return { supported, permission, isSubscribed, loading, subscribe, unsubscribe };
}
